import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Package, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';

const formatPrice = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const fieldTypes = [
  { value: 'text', label: 'Texte' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'textarea', label: 'Zone de texte' },
  { value: 'select', label: 'Choix unique' },
  { value: 'multiselect', label: 'Choix multiple' },
  { value: 'date', label: 'Date' }
];

export const OffersManagementPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    price: '',
    currency: 'USD',
    is_active: true,
    form_fields: []
  });

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await api.get('/offers?active_only=false');
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      price: '',
      currency: 'USD',
      is_active: true,
      form_fields: []
    });
    setEditingOffer(null);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      description: offer.description,
      image_url: offer.image_url || '',
      price: offer.price || '',
      currency: offer.currency || 'USD',
      is_active: offer.is_active,
      form_fields: offer.form_fields || []
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null
      };

      if (editingOffer) {
        await api.put(`/offers/${editingOffer.id}`, payload);
        toast.success('Offre mise à jour');
      } else {
        await api.post('/offers', payload);
        toast.success('Offre créée');
      }
      
      setShowForm(false);
      resetForm();
      fetchOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/offers/${deleteConfirm}`);
      toast.success('Offre supprimée');
      setDeleteConfirm(null);
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const addFormField = () => {
    const newField = {
      id: Date.now().toString(),
      label: '',
      field_type: 'text',
      required: true,
      options: [],
      placeholder: ''
    };
    setFormData({
      ...formData,
      form_fields: [...formData.form_fields, newField]
    });
  };

  const updateFormField = (index, updates) => {
    const updatedFields = [...formData.form_fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setFormData({ ...formData, form_fields: updatedFields });
  };

  const removeFormField = (index) => {
    const updatedFields = formData.form_fields.filter((_, i) => i !== index);
    setFormData({ ...formData, form_fields: updatedFields });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offers-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Offres</h2>
          <p className="text-gray-400">{offers.length} offres au total</p>
        </div>
        <Button 
          className="bg-sky-500 hover:bg-sky-600"
          onClick={openCreateForm}
          data-testid="create-offer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Offre
        </Button>
      </div>

      {/* Offers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offers.map((offer) => (
          <Card 
            key={offer.id} 
            className={`bg-[#0F172A] border-white/10 ${!offer.is_active ? 'opacity-60' : ''}`}
            data-testid={`offer-card-${offer.id}`}
          >
            {offer.image_url && (
              <div className="h-40 overflow-hidden rounded-t-lg">
                <img 
                  src={offer.image_url} 
                  alt={offer.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-white">{offer.title}</h3>
                {!offer.is_active && (
                  <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                )}
              </div>
              <p className="text-sm text-gray-400 line-clamp-2 mb-4">{offer.description}</p>
              
              {offer.price && (
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="w-4 h-4 text-sky-400" />
                  <span className="text-lg font-bold text-sky-400">
                    {formatPrice(offer.price, offer.currency)}
                  </span>
                </div>
              )}

              {offer.form_fields?.length > 0 && (
                <Badge className="bg-purple-500/20 text-purple-400 mb-4">
                  {offer.form_fields.length} champs personnalisés
                </Badge>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 border-white/10 text-gray-300 hover:text-white"
                  onClick={() => openEditForm(offer)}
                  data-testid={`edit-offer-${offer.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setDeleteConfirm(offer.id)}
                  data-testid={`delete-offer-${offer.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {offers.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune offre créée</p>
            <Button 
              className="mt-4 bg-sky-500 hover:bg-sky-600"
              onClick={openCreateForm}
            >
              Créer une offre
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); setShowForm(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Remplissez les informations de l'offre et configurez le formulaire personnalisé
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="offer-form">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="offer-title"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                  data-testid="offer-description"
                />
              </div>

              <div>
                <Label>URL de l'image</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="offer-image"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prix</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="offer-price"
                  />
                </div>
                <div>
                  <Label>Devise</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(value) => setFormData({...formData, currency: value})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/10">
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="offer-active"
                />
                <Label>Offre active</Label>
              </div>
            </div>

            {/* Form Builder */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-lg">Formulaire Personnalisé</Label>
                  <p className="text-sm text-gray-400">Ajoutez des champs pour collecter des informations spécifiques</p>
                </div>
                <Button 
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFormField}
                  className="border-sky-500 text-sky-400"
                  data-testid="add-field-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter un champ
                </Button>
              </div>

              {formData.form_fields.map((field, index) => (
                <Card key={field.id} className="bg-white/5 border-white/10">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Champ {index + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFormField(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Label</Label>
                        <Input
                          value={field.label}
                          onChange={(e) => updateFormField(index, { label: e.target.value })}
                          placeholder="Ex: Nom complet"
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Type</Label>
                        <Select 
                          value={field.field_type} 
                          onValueChange={(value) => updateFormField(index, { field_type: value })}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E293B] border-white/10">
                            {fieldTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {['select', 'multiselect'].includes(field.field_type) && (
                      <div>
                        <Label className="text-sm">Options (séparées par des virgules)</Label>
                        <Input
                          value={field.options?.join(', ') || ''}
                          onChange={(e) => updateFormField(index, { 
                            options: e.target.value.split(',').map(o => o.trim()).filter(Boolean)
                          })}
                          placeholder="Option 1, Option 2, Option 3"
                          className="bg-white/5 border-white/10 text-white"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={field.required}
                        onCheckedChange={(checked) => updateFormField(index, { required: checked })}
                      />
                      <Label className="text-sm">Champ requis</Label>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { resetForm(); setShowForm(false); }}
                className="border-white/10 text-gray-300"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="save-offer-btn"
              >
                {editingOffer ? 'Mettre à jour' : 'Créer l\'offre'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-[#0F172A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette offre?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Cette action est irréversible. L'offre sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-gray-300">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OffersManagementPage;
